import { toast } from 'sonner';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import {
  can,
  deleteMyAvatar,
  errorMessage,
  NotLoggedInError,
  putMyAvatar,
  ROLE_LABEL,
  updateMe,
  type Project,
  type User,
} from './api';
import { AssigneeMark } from './board/Chips';
import { PeopleCard } from './settings/PeopleCard';
import { LabelsCard, ProjectsCard } from './settings/Workspace';
import { THEMES, getTheme, setTheme, getMode, setMode, resolvedMode, type ThemeId, type ThemeMode } from './theme';

/* ============================================================
   설정 — 내 것과 함께 쓰는 것
   ------------------------------------------------------------
   **위 둘은 「나」다**(닉네임·테마). 남에게 영향을 주지 않는다.

   **아래 셋은 함께 쓰는 것이고 권한이 있어야 보인다** — 프로젝트
   (`project.create`) · 라벨(`label.manage`) · 사람(`user.view`).
   2026-08-26 에 `/projects` 페이지를 걷고 여기로 들여왔다(지시).
   옛 판이 그것을 따로 둔 이유는 「설정은 내 것」이었는데, 그 가름 때문에
   **이 도구의 단위가 프로젝트인 것처럼 보였다.** 단위는 티켓이다.

   ⚠ **화면이 카드를 숨기는 것은 방어가 아니다.** 막는 것은 서버다
   (`require_perm`) — 여기서 숨겨도 API 는 열려 있다.
   ============================================================ */


const NICKNAME_MAX = 40;

/**
 * 나눈 축은 **누구의 것인가**다 — 내 것 · 함께 쓰는 것 · 사람 · 기계.
 *
 * ⚠ **주소에 남는 값이다**(`?tab=`). 이름을 바꾸면 남이 보낸 링크가 첫 탭으로
 * 떨어진다 — 깨지지는 않지만 그 자리로 못 간다.
 */
type SettingsTab = 'me' | 'workspace' | 'people';



/**
 * 닉네임 검사. **비어 있어도 된다** — 그때는 이름을 쓴다는 뜻이다.
 * 옛 「표시 이름」은 비울 수 없었는데, 그건 이름을 덮어쓰던 시절의 규칙이다.
 */
function nicknameIssue(value: string): string | null {
  if (value.trim().length > NICKNAME_MAX) return `닉네임은 ${NICKNAME_MAX}자를 넘을 수 없다`;
  return null;
}

type Props = {
  user: User;
  onSaved: (next: User) => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
};

type PageProps = Props & {
  projects: Project[];
  projectId: number | null;
  reloadProjects: () => Promise<void>;
};

export function SettingsPage({
  user,
  onSaved,
  onError,
  onUnauthorized,
  projects,
  projectId,
  reloadProjects,
}: PageProps) {
  const current = projects.find((p) => p.id === projectId) ?? null;
  /** 볼 것이 있는 탭만. 권한이 없으면 카드가 안 보이는데 탭만 남으면 빈 화면이다. */
  const tabs = useMemo(() => {
    const out: { id: SettingsTab; label: string }[] = [{ id: 'me', label: '내 것' }];
    if (can(user, 'project.create') || can(user, 'label.manage')) {
      out.push({ id: 'workspace', label: '작업 공간' });
    }
    if (can(user, 'user.view')) out.push({ id: 'people', label: '사람' });
    return out;
  }, [user]);

  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') as SettingsTab | null;
  // 주소에 없는 탭(권한이 빠진 뒤 남은 옛 링크)은 첫 탭으로 떨어뜨린다.
  const tab: SettingsTab = tabs.some((t) => t.id === asked) ? asked! : tabs[0].id;

  function setTab(next: SettingsTab) {
    const copy = new URLSearchParams(params);
    copy.set('tab', next);
    setParams(copy, { replace: true });
  }

  return (
    <>
      <nav className="mb-s3 flex items-center gap-s2 text-meta text-ink-3" aria-label="위치">
        <Link to="/" className="text-ink-3 no-underline hover:text-brand">
          작업
        </Link>
        <span aria-hidden="true">›</span>
        <span>설정</span>
      </nav>

      <div className="mb-s5 flex items-center gap-s3">
        <h1 className="m-0 text-h1 font-bold text-ink">설정</h1>
      </div>

      {/* ============================================================
          탭 — 화면이 너무 길었다 (2026-08-31 지시)
          ------------------------------------------------------------
          카드 여섯이 한 줄로 쌓여 있어 「사람」을 보려면 끝까지 굴려야
          했다. 나눈 축은 **누구의 것인가**다 — 내 것 · 함께 쓰는 것 ·
          기계가 쓰는 것.

          **볼 것이 없는 탭은 안 그린다.** 권한이 없으면 카드가 안 보이는데
          탭만 남아 있으면 눌러도 빈 화면이 나온다.

          ⚠ **탭을 주소에 남긴다**(`?tab=`). 안 남기면 새로고침마다 첫 탭으로
          돌아가고, 남에게 「설정의 그 자리」를 링크로 못 준다.

          ⚠ **화면이 탭을 숨기는 것은 방어가 아니다.** 막는 것은 서버다 —
          이 파일 머리말이 같은 말을 하고 있다.
          ============================================================ */}
      <div className="mb-s5 inline-flex overflow-hidden rounded-md border border-line">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={tab === item.id}
            onClick={() => setTab(item.id)}
            className={
              tab === item.id
                ? 'bg-brand px-s4 py-[6px] text-meta text-on-brand max-[720px]:min-h-[44px]'
                : 'bg-panel px-s4 py-[6px] text-meta text-ink-2 hover:bg-sunk max-[720px]:min-h-[44px]'
            }
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex max-w-[720px] flex-col gap-s5">
        {tab === 'me' && (
          <>
            <NicknameCard
              user={user}
              onSaved={onSaved}
              onError={onError}
              onUnauthorized={onUnauthorized}
            />
            <ThemeCard />
            <AccountCard user={user} />
          </>
        )}

        {tab === 'workspace' && (
          <>
            {can(user, 'project.create') && (
              <ProjectsCard
                projects={projects}
                projectId={projectId}
                reloadProjects={reloadProjects}
                onError={onError}
                onUnauthorized={onUnauthorized}
              />
            )}
            {can(user, 'label.manage') && current && (
              <LabelsCard
                project={current}
                onError={onError}
                onUnauthorized={onUnauthorized}
              />
            )}
          </>
        )}

        {tab === 'people' && can(user, 'user.view') && (
          <PeopleCard
            meId={user.id}
            canGrant={can(user, 'user.grant')}
            canSetRole={can(user, 'user.role')}
            onError={onError}
            onUnauthorized={onUnauthorized}
          />
        )}
      </div>
    </>
  );
}

/* ── 닉네임 ─────────────────────────────────────────────── */

function NicknameCard({ user, onSaved, onError, onUnauthorized }: Props) {
  // 안 정했으면 빈 칸이다. 이름을 미리 채우면 「지우기」와 「이름으로 두기」가
  // 구분이 안 된다 — 지우려 해도 그 이름이 그대로 저장될 뿐이다.
  //
  // 저장 뒤에도 이 값을 다시 안 맞춘다. 저장이 성공하면 초안과 서버 값이 이미
  // 같고, `dirty` 가 false 가 되어 단추가 잠긴다 — `useEffect` 로 되맞추면
  // 사람이 타이핑하는 중에 값이 튈 수 있다.
  const [draft, setDraft] = useState(user.nickname ?? '');
  const [busy, setBusy] = useState(false);

  const issue = nicknameIssue(draft);
  const next = draft.trim() || null;
  const dirty = next !== (user.nickname ?? null);
  const canSave = issue === null && dirty && !busy;

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave) return;
    setBusy(true);
    onError(null);
    try {
      // 비었으면 `null` — 서버가 닉네임을 지우고 이름으로 돌아간다.
      onSaved(await updateMe(next));
      toast.success('닉네임을 저장했다');
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`닉네임을 저장하지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-s4 pt-s5">
        {/* **사진과 이름이 한 카드다.** 둘 다 「나를 어떻게 보이게 할까」이고,
            따로 두면 설정 화면에 내 카드가 둘이 된다. */}
        <AvatarField
          user={user}
          onSaved={onSaved}
          onError={onError}
          onUnauthorized={onUnauthorized}
        />

        <form
          className="flex flex-col gap-s3 border-t border-line pt-s4"
          onSubmit={(event) => void handleSave(event)}
        >
          <div className="flex flex-col gap-s2">
            {/* **이름표지 소제목이 아니다.** 굵게 세우면 카드 제목처럼
                읽힌다 — 다른 화면의 `<dt>` 와 같은 무게로 둔다. */}
            <Label htmlFor="nickname" className="text-meta text-ink-3">
              보드와 기록에 나올 이름
            </Label>
            <Input
              id="nickname"
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              placeholder={user.name}
              autoComplete="nickname"
              disabled={busy}
              aria-describedby="nickname-hint"
              aria-invalid={issue !== null}
              className="max-w-[320px]"
            />
            {issue ? (
              <p className="m-0 text-small text-st-red-f" role="alert">
                {issue}
              </p>
            ) : (
              <p id="nickname-hint" className="m-0 text-small text-ink-3">
                {draft.trim()
                  ? '티켓 카드와 사이드바에 이 이름으로 표시됨'
                  : `비워 두면 이름(${user.name})으로 표시됨`}
              </p>
            )}
          </div>

          {/* **긍정은 오른쪽 끝이다**(2026-09-16 사용자 지정). 옛 판은
              「저장」이 「되돌리기」 앞에 있었다 — 눈이 먼저 닿는 자리에
              되돌리는 단추가 서면 저장한 줄 알고 나가게 된다. */}
          <div className="flex items-center gap-s3">
            {dirty && !busy && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraft(user.nickname ?? '');
                }}
              >
                되돌리기
              </Button>
            )}
            <Button type="submit" disabled={!canSave}>
              {busy ? '저장 중…' : '저장'}
            </Button>
            {/* ⚠ **여기 있던 「저장했다」 글자는 토스트로 옮겼다**
                (2026-08-26 지시). 알림 자리가 화면마다 다르면 사람이 어디를
                봐야 할지 매번 새로 배운다 — 전부 오른쪽 위로 모았다. */}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ── 프로필 사진 ────────────────────────────────────────── */

/**
 * 아바타 한 변. **정사각으로 줄여서 보낸다.**
 *
 * 128px 인 이유는 카드에 20px, 상세에 24px 로 그리는데 고해상도 화면이
 * 그 두세 배를 쓰기 때문이다. 더 키우면 표에 담는 바이트만 늘고 눈에는
 * 차이가 없다.
 */
const AVATAR_PX = 128;

/**
 * 고른 파일을 **브라우저에서** 정사각 PNG 로 줄인다.
 *
 * **왜 서버가 안 하나** — 서버에서 줄이려면 Pillow 를 새로 깔아야 하고,
 * 그 의존성 하나가 이미지 파서를 통째로 들이는 일이라 공격 표면이 는다.
 * 브라우저는 그 일을 이미 할 줄 안다.
 *
 * ⚠ **가운데를 자른다.** 통째로 우겨넣으면 세로 사진이 납작해진다 —
 * 얼굴은 대개 가운데에 있다.
 */
async function toSquarePng(file: File): Promise<string> {
  // `from-image` 가 없으면 아이폰으로 찍은 세로 사진이 눕는다(EXIF 회전).
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_PX;
  canvas.height = AVATAR_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('이 브라우저에서는 사진을 줄일 수 없다');
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    AVATAR_PX,
    AVATAR_PX,
  );
  bitmap.close();
  return canvas.toDataURL('image/png');
}

function AvatarField({ user, onSaved, onError, onUnauthorized }: Props) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function run(work: () => Promise<User>, done: string) {
    setBusy(true);
    onError(null);
    try {
      onSaved(await work());
      toast.success(done);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`사진을 바꾸지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handlePick(file: File | undefined) {
    if (!file) return;
    // 파일이 아무리 커도 줄인 뒤에는 수십 KB 다 — **여기서 크기를 안 막는다.**
    // 막으면 「사진이 큰데요」라는 말을 사람이 직접 풀어야 한다.
    await run(async () => putMyAvatar(await toSquarePng(file)), '사진을 바꿨다');
  }

  return (
    <div className="flex items-center gap-s4">
      {/* 실제로 카드에 서는 것보다 크게 보여 준다 — 20px 로 보여 주면
          무엇을 골랐는지 확인이 안 된다. */}
      <AssigneeMark
        user={{
          id: user.id,
          display_name: user.display_name,
          initial: user.display_name.slice(0, 1).toUpperCase(),
          role: user.role,
          avatar_url: user.avatar_url,
        }}
        size={56}
      />

      <div className="flex flex-col gap-s2">
        <div className="flex items-center gap-s2">
          {/* ⚠ **파일 입력을 감춰 두고 단추로 부른다.** 브라우저 기본
              모양은 테마 일곱 어디에도 안 맞고 글자도 우리 것이 아니다. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="sr-only"
            aria-label="프로필 사진 고르기"
            onChange={(event) => {
              void handlePick(event.target.files?.[0]);
              // 같은 파일을 다시 고를 수 있게 비운다 — 안 비우면 값이
              // 그대로라 `change` 가 안 뜬다.
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? '바꾸는 중…' : user.avatar_url ? '사진 바꾸기' : '사진 고르기'}
          </Button>
          {user.avatar_url && (
            <Button
              type="button"
              variant="ghost"
              className="text-ink-3"
              disabled={busy}
              onClick={() => void run(deleteMyAvatar, '사진을 지웠다')}
            >
              지우기
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── 화면 ───────────────────────────────────────────────── */

function ThemeCard() {
  const [current, setCurrent] = useState<ThemeId>(getTheme);

  const [mode, setCurrentMode] = useState<ThemeMode>(getMode);
  const [brightness, setBrightness] = useState(resolvedMode);
  useEffect(() => {
    const sync = () => { setCurrent(getTheme()); setCurrentMode(getMode()); setBrightness(resolvedMode()); };
    window.addEventListener('office-theme-change', sync);
    return () => window.removeEventListener('office-theme-change', sync);
  }, []);

  function pick(id: ThemeId) {
    setTheme(id);
    setCurrent(id);
  }

  return (
    <Card>
      <CardContent className="pt-s5">
        <div role="group" aria-label="밝기 모드" className="mb-s5 flex flex-wrap gap-s2">
          {([{ id: 'light', label: '라이트 모드' }, { id: 'dark', label: '다크 모드' }, { id: 'system', label: '시스템 설정' }] as const).map(item => (
            <button key={item.id} type="button" aria-pressed={mode === item.id}
              onClick={() => setMode(item.id)}
              className={cn('rounded-md border px-s4 py-s2 text-meta', mode === item.id ? 'border-brand bg-brand-t text-ink' : 'border-line bg-panel text-ink-2 hover:bg-sunk')}>
              {item.label}
            </button>
          ))}
        </div>
        <p className="mb-s3 text-meta text-ink-2">색상 테마 · 10종 · 현재 {brightness === 'dark' ? '다크' : '라이트'} 모드</p>
        <div
          className="grid grid-cols-2 gap-s2 max-[720px]:grid-cols-1"
          role="group"
          aria-label="테마"
        >
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={current === t.id}
              onClick={() => pick(t.id)}
              className={cn(
                'flex items-center gap-s3 rounded-md border px-s3 py-s2 text-left text-meta',
                'max-[720px]:min-h-[44px]',
                current === t.id
                  ? 'border-brand bg-brand-t text-ink'
                  : 'border-line bg-panel text-ink-2 hover:bg-sunk',
              )}
            >
              {/* 색 견본은 **그 테마의 값**을 인라인으로 칠한다. 토큰으로 그리면
                  지금 켜진 테마의 색이 전부에 나와 목록이 다 똑같아 보인다. */}
              <span
                aria-hidden="true"
                className="flex flex-none overflow-hidden rounded-sm border border-line"
              >
                {[t.light, t.dark].map((c) => (
                  <span key={c} className="block size-s4" style={{ background: c }} />
                ))}
              </span>
              <span className="min-w-0 flex-1">{t.label}</span>
              <span className="flex-none text-micro text-ink-3">
                {current === t.id ? '선택됨' : '라이트·다크'}
              </span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── 계정 ───────────────────────────────────────────────── */

function AccountCard({ user }: { user: User }) {
  return (
    <Card>
      <CardContent className="pt-s5">
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-s5 gap-y-s3 text-meta">
          {/* **이름은 못 고친다.** Google 이 준 값이고, 덮어쓰면 되찾을 길이
              없다 — 로그인은 이미 있는 사용자의 이름을 다시 안 채운다. */}
          <dt className="text-ink-3">이름</dt>
          <dd className="m-0 text-ink">
            {user.name}
          </dd>

          <dt className="text-ink-3">이메일</dt>
          <dd className="m-0 text-ink">{user.email}</dd>

        </dl>

        {/* ⚠ **권한 줄을 지웠다** (2026-08-26). 권한 층 자체를 걷어냈고,
            없는 것을 「0개」로 그리면 「권한이 없어서 못 한다」로 읽힌다.
            다시 얹을 때 이 자리로 돌아온다. */}
        {/* 역할은 여기서 못 고친다 — 관리자가 아래 「사람」 카드에서 옮긴다.
            내 것을 내가 고칠 수 있게 두면 화면 둘이 같은 값을 쥔다. */}
        <dl className="m-0 mt-s3 grid grid-cols-[auto_1fr] gap-x-s5 gap-y-s3 text-meta">
          <dt className="text-ink-3">역할</dt>
          <dd className="m-0 text-ink">{ROLE_LABEL[user.role]}</dd>

          {/* **가진 권한을 그대로 보여 준다.** 「무엇을 할 수 있나」를 묻는
              자리에서 숫자만 적으면 다시 누군가에게 물어야 한다. */}
          <dt className="text-ink-3">권한</dt>
          <dd className="m-0 text-ink">
            {user.is_admin ? (
              <>
                전부
              </>
            ) : (
              <span className="tnum">{user.permissions.length}개</span>
            )}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}
